import React, {useState} from 'react';
import {useNavigate, Link} from 'react-router-dom';
import {useAuth} from '../contexts/AuthContext';
import {Star, Mail, Lock, Eye, EyeOff, ArrowRight, Loader2} from 'lucide-react';
import {useColorTheme} from '../contexts/ColorThemeContext';
import {cn} from '../util/designUtil';
import toast from 'react-hot-toast';

const Card: React.FC<{className?: string; children: React.ReactNode}> = ({className, children}) => (
  <div className={cn("rounded-lg border bg-card text-card-foreground shadow-sm", className)}>{children}</div>
);

const CardHeader: React.FC<{children: React.ReactNode}> = ({children}) => <div className="flex flex-col space-y-1.5 p-6">{children}</div>;

const CardTitle: React.FC<{children: React.ReactNode; className?: string}> = ({children, className}) => <h3 className={cn("text-2xl font-semibold leading-none tracking-tight", className)}>{children}</h3>;

const CardDescription: React.FC<{children: React.ReactNode; className?: string}> = ({children, className}) => <p className={cn("text-sm text-muted-foreground", className)}>{children}</p>;

const CardContent: React.FC<{className?: string; children: React.ReactNode}> = ({className, children}) => <div className={cn("p-6 pt-0", className)}>{children}</div>;

const CardFooter: React.FC<{className?: string; children: React.ReactNode}> = ({className, children}) => <div className={cn("flex items-center p-6 pt-0", className)}>{children}</div>;

const Input: React.FC<React.InputHTMLAttributes<HTMLInputElement> & {icon?: React.ReactNode}> = ({className, type, icon, ...props}) => {
  return (
    <div className="relative">
      {icon && <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 h-4 w-4">{icon}</div>}
      <input
        type={type}
        className={cn(
          "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
          icon ? "pl-10" : "",
          className
        )}
        {...props}
      />
    </div>
  );
};

const Button: React.FC<React.ButtonHTMLAttributes<HTMLButtonElement> & {children: React.ReactNode}> = ({className, children, ...props}) => (
  <button
    className={cn(
      "inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
      "h-10 px-4 py-2",
      className
    )}
    {...props}
  >
    {children}
  </button>
);

const LoginScreen: React.FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const {login, loginWithGoogle} = useAuth();
  const navigate = useNavigate();
  const {theme} = useColorTheme();

  const getFirebaseErrorMessage = (error: any): string => {
    const code = error?.code;
    switch (code) {
      case 'auth/invalid-email': return 'Invalid email address.';
      case 'auth/user-disabled': return 'This account has been disabled.';
      case 'auth/user-not-found': return 'No account found with this email.';
      case 'auth/wrong-password': return 'Incorrect password.';
      case 'auth/invalid-credential': return 'Invalid email or password.';
      case 'auth/too-many-requests': return 'Too many attempts. Please try again later.';
      default: return 'Sign in failed. Please try again.';
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      navigate('/home');
    } catch (error: any) {
      toast.error(getFirebaseErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 py-6 sm:py-8 px-4">
      <div className="w-full max-w-md">
        {/* <div className="flex justify-center mb-8">
          <Link to="/" className="flex items-center">
            <Star className={cn("h-8 w-8", theme.secondary)} />
            <span
              className={cn("ml-2 text-2xl font-bold bg-gradient-to-r bg-clip-text text-transparent", theme.primary)}
            >
              Nexbii
            </span>
          </Link>
        </div> */}

        <Card className="border-gray-200">
          <CardHeader>
            <CardTitle className="text-2xl text-center">Sign In</CardTitle>
            <CardDescription className="text-center">
              Welcome back! Please enter your details.
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-4 pt-6">
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Input
                  icon={<Mail />}
                  id="email"
                  type="email"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="border-gray-300"
                />
              </div>
              <div className="space-y-2">
                <div className="relative">
                  <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 h-4 w-4"><Lock/></div>
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="flex h-10 w-full rounded-md border border-gray-300 bg-background px-3 py-2 pl-10 pr-10 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>
              <div className="flex justify-end">
                <Link to="/forgot-password" className={cn("text-sm underline", theme.secondary)}>
                  Forgot password?
                </Link>
              </div>
              <Button type="submit" disabled={loading} className={cn("w-full bg-gradient-to-r text-white", theme.primary)}>
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  <>
                    Sign In
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </>
                )}
              </Button>
            </form>
          </CardContent>
          
          <div className="px-6 pb-6">
            <div className="relative my-4">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="bg-gray-100 px-2 text-gray-500">
                  Or continue with
                </span>
              </div>
            </div>

            <Button
              type="button"
              disabled={loading}
              onClick={() => {
                setLoading(true);
                loginWithGoogle()
                  .then(() => navigate('/home'))
                  .catch((error: any) => toast.error('Google sign-in failed. Please try again.'))
                  .finally(() => setLoading(false));
              }}
              className="w-full border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
            >
              <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                <path
                  fill="currentColor"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="currentColor"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="currentColor"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="currentColor"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              Sign in with Google
            </Button>
          </div>

          <CardFooter className="flex-col items-stretch pt-0">
            <div className="text-center text-sm text-gray-500">
              Don't have an account?{' '}
              <Link to="/register" className={cn("font-medium underline", theme.secondary)}>
                Sign up
              </Link>
            </div>
          </CardFooter>
        </Card>

        <p className="text-center text-sm text-gray-500 mt-6">
          By continuing, you agree to Nexbii's{" "}
          <Link to="/terms" className={cn("underline", theme.secondary)}>
            Terms of Service
          </Link>{" "}
          and{" "}
          <Link to="/privacy" className={cn("underline", theme.secondary)}>
            Privacy Policy
          </Link>
          .
        </p>
      </div>
    </div>
  );
};

export default LoginScreen;
